<template>
  <v-dialog
    v-model="innerOpen"
    fullscreen
    hide-overlay
    transition="dialog-bottom-transition"
    content-class="mobile-ai-chat-dialog"
  >
    <div class="mobile-ai-chat mobile-ai-chat-root d-flex flex-column fill-height">
      <!-- 헤더: 제목 · 새 대화 · 닫기 -->
      <v-app-bar
        flat
        color="transparent"
        class="mobile-ai-chat__appbar mobile-ai-chat-appbar flex-shrink-0"
      >
        <div class="mobile-ai-chat__header-inner mobile-ai-chat-header-wrap">
          <div class="mobile-ai-chat-header-row1 d-flex align-center">
            <div class="subtitle-1 font-weight-bold mobile-ai-chat-header-title">
              {{ 'AI 업무 지원' | translate }}
            </div>
            <v-spacer />
            <v-btn
              small
              outlined
              class="mobile-ai-chat-header-btn"
              :disabled="loadingChat"
              @click="startNewChat"
            >
              {{ '새 대화' | translate }}
            </v-btn>
            <v-btn
              icon
              aria-label="close"
              class="mobile-ai-chat-header-close-btn"
              @click="close"
            >
              <v-icon small>
                mdi-close
              </v-icon>
            </v-btn>
          </div>
          <div class="caption mobile-ai-chat-header-row2">
            <span class="mobile-ai-credit-label">{{ '크레딧' | translate }}</span>
            <span class="mobile-ai-credit-text">{{ availableCreditDisplay }}</span>
          </div>
          <div class="mobile-ai-chat__disclaimer caption">
            {{ '답변은 참고용이며, 상세 목록은 PC 메뉴에서 확인하세요.' | translate }}
          </div>
        </div>
      </v-app-bar>

      <!-- 본문: 메시지 -->
      <div
        ref="scrollArea"
        class="mobile-ai-chat__scroll mobile-ai-chat-body flex-grow-1 overflow-y-auto d-flex flex-column"
      >
        <div
          class="mobile-ai-chat__scroll-inner flex-grow-1"
          :class="{
            'mobile-ai-chat__scroll-inner--credit-empty': showCreditEmptyNotice,
          }"
        >
          <div
            v-if="showCreditEmptyNotice"
            class="mobile-ai-chat-credit-notice"
          >
            <p class="mobile-ai-chat-credit-notice__title">
              {{ '크레딧이 부족합니다.' | translate }}
            </p>
            <p class="mobile-ai-chat-credit-notice__sub">
              {{ 'PC웹에서 충전 후 이용해 주세요.' | translate }}
            </p>
          </div>
          <div
            v-if="messages.length || loadingChat"
            class="mobile-ai-chat__messages pa-4 pb-16"
          >
            <div
              v-for="(m, idx) in messages"
              :key="'msg-' + idx"
              class="mobile-ai-chat__msg"
              :class="m.role === 'user' ? 'mobile-ai-chat__msg--user' : 'mobile-ai-chat__msg--assistant'"
            >
              <v-sheet
                v-if="m.role === 'user'"
                flat
                rounded
                class="mobile-ai-chat__bubble mobile-ai-chat__bubble--user d-inline-block pa-3 text-left mobile-ai-chat-bubble-user"
              >
                <div
                  class="body-2"
                  style="white-space: pre-wrap; word-break: break-word;"
                >
                  {{ m.text }}
                </div>
              </v-sheet>
              <div v-else>
                <v-sheet
                  flat
                  rounded
                  class="mobile-ai-chat__bubble mobile-ai-chat__bubble--bot pa-3 mobile-ai-chat-bubble-bot"
                >
                  <div class="body-2 mobile-ai-md">
                    <template v-for="(b, bi) in markdownBlocks(m.text)">
                      <h1
                        v-if="b.type === 'h1'"
                        :key="'md-h1-' + idx + '-' + bi"
                        class="mobile-ai-md-h1"
                        v-html="b.html"
                      />
                      <h2
                        v-else-if="b.type === 'h2'"
                        :key="'md-h2-' + idx + '-' + bi"
                        class="mobile-ai-md-h2"
                        v-html="b.html"
                      />
                      <h3
                        v-else-if="b.type === 'h3'"
                        :key="'md-h3-' + idx + '-' + bi"
                        class="mobile-ai-md-h3"
                        v-html="b.html"
                      />
                      <h4
                        v-else-if="b.type === 'h4'"
                        :key="'md-h4-' + idx + '-' + bi"
                        class="mobile-ai-md-h4"
                        v-html="b.html"
                      />
                      <ul
                        v-else-if="b.type === 'ul'"
                        :key="'md-ul-' + idx + '-' + bi"
                        class="mobile-ai-md-list"
                      >
                        <li
                          v-for="(item, li) in b.items"
                          :key="'md-li-' + idx + '-' + bi + '-' + li"
                          v-html="item"
                        />
                      </ul>
                      <ol
                        v-else-if="b.type === 'ol'"
                        :key="'md-ol-' + idx + '-' + bi"
                        class="mobile-ai-md-list"
                      >
                        <li
                          v-for="(item, li) in b.items"
                          :key="'md-oi-' + idx + '-' + bi + '-' + li"
                          v-html="item"
                        />
                      </ol>
                      <div
                        v-else-if="b.type === 'br'"
                        :key="'md-br-' + idx + '-' + bi"
                        class="mobile-ai-md-br"
                      />
                      <p
                        v-else
                        :key="'md-p-' + idx + '-' + bi"
                        class="mobile-ai-md-p"
                        v-html="b.html"
                      />
                    </template>
                  </div>
                  <div
                    v-if="visibleMobileAiCards(m).length"
                    class="mobile-ai-chat__answer-list mt-2 mb-3"
                  >
                    <v-card
                      v-for="(c, ci) in visibleMobileAiCards(m)"
                      :key="'c-' + ci"
                      flat
                      class="mobile-ai-chat__stat-card pa-3"
                    >
                      <div class="mobile-ai-chat__stat-card-title caption">
                        {{ c.title }}
                      </div>
                      <div class="mobile-ai-chat__stat-card-value">
                        {{ c.value }}
                      </div>
                      <div
                        v-if="c.subtitle"
                        class="mobile-ai-chat__stat-card-sub caption"
                      >
                        {{ c.subtitle }}
                      </div>
                    </v-card>
                  </div>
                  <div
                    v-if="m.tables && m.tables.length"
                    class="mobile-ai-chat-tables mt-2"
                  >
                    <div
                      v-for="(tbl, ti) in m.tables"
                      :key="'tbl-' + idx + '-' + ti"
                      class="mb-2"
                    >
                      <template v-if="resolveTableColumns(tbl).length">
                        <div
                          v-if="tbl.title"
                          class="caption font-weight-medium grey--text text--darken-2 mb-2"
                        >
                          {{ tbl.title }}
                        </div>
                        <div class="mobile-ai-chat__answer-list mb-2">
                          <v-card
                            v-for="(row, ri) in visibleTableRows(tbl)"
                            :key="'trow-' + idx + '-' + ti + '-' + ri"
                            flat
                            class="mobile-ai-chat__panel-card pa-3 mobile-ai-table-row-card"
                          >
                            <div
                              class="mobile-ai-table-row-head d-flex align-center flex-nowrap min-width-0"
                              :class="{
                                'mobile-ai-table-row-head--interactive':
                                  secondaryMobileColumns(tbl).length,
                              }"
                              :tabindex="secondaryMobileColumns(tbl).length ? 0 : -1"
                              :role="
                                secondaryMobileColumns(tbl).length ? 'button' : null
                              "
                              :aria-expanded="
                                secondaryMobileColumns(tbl).length
                                  ? isMobileAiRowExpanded(idx, ti, ri)
                                    ? 'true'
                                    : 'false'
                                  : null
                              "
                              @click="onMobileAiTableRowHeadClick(idx, ti, ri, tbl)"
                              @keydown.enter.prevent="
                                onMobileAiTableRowHeadClick(idx, ti, ri, tbl)
                              "
                              @keydown.space.prevent="
                                onMobileAiTableRowHeadClick(idx, ti, ri, tbl)
                              "
                            >
                              <div class="mobile-ai-table-row-head-main flex-grow-1 min-width-0">
                                <div
                                  v-for="col in primaryMobileColumns(tbl)"
                                  :key="'tpri-' + ti + '-' + ri + '-' + col.key"
                                  class="mobile-ai-table-row-inline"
                                >
                                  <span class="caption grey--text text--darken-1 mobile-ai-table-row-inline-label">
                                    {{ col.title || col.key }}
                                  </span>
                                  <span class="body-2 font-weight-medium mobile-ai-table-row-inline-value">
                                    {{ formatAiTableCell(row[col.key], col) }}
                                  </span>
                                </div>
                              </div>
                              <div
                                v-if="secondaryMobileColumns(tbl).length"
                                class="flex-shrink-0 mobile-ai-table-row-chevron"
                                aria-hidden="true"
                              >
                                <v-icon small>
                                  {{ mobileAiRowExpandChevron(idx, ti, ri) }}
                                </v-icon>
                              </div>
                            </div>
                            <v-expand-transition>
                              <div
                                v-show="isMobileAiRowExpanded(idx, ti, ri) && secondaryMobileColumns(tbl).length"
                                class="mobile-ai-table-secondary-wrap"
                              >
                                <div
                                  v-for="col in secondaryMobileColumns(tbl)"
                                  :key="'tsec-' + ti + '-' + ri + '-' + col.key"
                                  class="mobile-ai-table-field-secondary"
                                >
                                  <div class="caption grey--text text--darken-1">
                                    {{ col.title || col.key }}
                                  </div>
                                  <div class="body-2 font-weight-medium mobile-ai-table-cell-value mobile-ai-table-cell-value--clamped">
                                    {{ formatAiTableCell(row[col.key], col) }}
                                  </div>
                                </div>
                              </div>
                            </v-expand-transition>
                          </v-card>
                        </div>
                        <div
                          v-if="tableRowsWereTruncated(tbl)"
                          class="caption grey--text mt-1"
                        >
                          {{ '일부 행만 표시합니다. 전체는 PC에서 확인하세요.' | translate }}
                        </div>
                        <div
                          v-if="tbl.summary && Object.keys(tbl.summary).length > 0"
                          class="caption grey--text mt-2 d-flex flex-wrap"
                        >
                          <span
                            v-for="(sv, sk) in tbl.summary"
                            :key="'tsum-' + ti + '-' + sk"
                            class="mr-3 mb-1"
                          >
                            <strong>{{ formatMobileAiSummaryFieldLabel(sk, aiResponseDomain(m)) }}</strong>
                            : {{ formatMobileAiSummaryValue(sk, sv) }}
                          </span>
                        </div>
                      </template>
                    </div>
                  </div>
                  <div
                    v-if="formatMobileSummaryItems(m.summary, aiResponseDomain(m)).length"
                    class="mt-2"
                  >
                    <v-card
                      flat
                      class="mobile-ai-chat__panel-card pa-3 mb-3 mobile-ai-summary-card"
                    >
                      <div class="caption mb-1 mobile-ai-summary-title">
                        {{ '요약' | translate }}
                      </div>
                      <div class="caption d-flex flex-wrap mobile-ai-summary-items">
                        <span
                          v-for="(s, si) in formatMobileSummaryItems(m.summary, aiResponseDomain(m))"
                          :key="'msum-' + idx + '-' + si"
                          class="mr-3 mb-1 mobile-ai-summary-item"
                        >
                          <strong>{{ s.label }}</strong>: {{ s.value }}
                        </span>
                      </div>
                    </v-card>
                  </div>
                </v-sheet>
              </div>
            </div>

            <div
              v-if="loadingChat"
              class="pa-3 grey lighten-4 rounded caption"
            >
              {{ '답변 생성 중…' | translate }}
            </div>
          </div>
        </div>
      </div>

      <div class="mobile-ai-chat__quick-panel mobile-ai-chat-quick-panel px-4 pt-3 pb-2 flex-shrink-0">
        <div class="mobile-ai-chat-quick-row mb-1">
          <span class="caption grey--text mobile-ai-chat-quick-label">
            {{ '추천' | translate }}
          </span>
          <div class="mobile-ai-chat-quick-scroll">
            <v-chip
              v-for="(s, j) in suggestionList"
              :key="'sg-bottom-' + j"
              small
              outlined
              class="mr-1 mobile-ai-chat-quick-chip"
              :disabled="loadingChat || chatDisabled"
              @click="sendMessage(s)"
            >
              {{ s }}
            </v-chip>
          </div>
        </div>
        <div class="mobile-ai-chat-quick-row">
          <span class="caption grey--text mobile-ai-chat-quick-label">
            {{ '최근' | translate }}
          </span>
          <div
            v-if="loadingRecent"
            class="caption grey--text mobile-ai-chat-quick-loading"
          >
            {{ '불러오는 중…' | translate }}
          </div>
          <div
            v-else-if="recentQuestions.length"
            class="mobile-ai-chat-quick-scroll"
          >
            <v-chip
              v-for="(q, i) in recentQuestions"
              :key="'rq-bottom-' + i"
              small
              class="mr-1 mobile-ai-chat-quick-chip"
              :disabled="loadingChat || chatDisabled"
              @click="sendMessage(q)"
            >
              {{ q }}
            </v-chip>
          </div>
          <div
            v-else
            class="caption grey--text mobile-ai-chat-quick-loading"
          >
            {{ '최근 질문 없음' | translate }}
          </div>
        </div>
      </div>

      <!-- 입력: 하단 고정 -->
      <div class="mobile-ai-chat__footer mobile-ai-chat-footer flex-shrink-0 pt-3 pb-safe">
        <div class="mobile-ai-chat__composer px-4 d-flex align-end">
          <v-textarea
            v-model="questionText"
            outlined
            dense
            hide-details
            auto-grow
            rows="1"
            row-height="22"
            max-rows="5"
            class="mobile-ai-chat__input mobile-ai-chat-input flex-grow-1"
            :placeholder="chatInputPlaceholder"
            :disabled="loadingChat || chatDisabled"
            @keydown.enter.exact.prevent="onEnterSend"
          />
          <v-btn
            color="primary"
            class="mobile-ai-chat__send-btn ml-3 flex-shrink-0"
            fab
            depressed
            :loading="loadingChat"
            :disabled="!canSend"
            aria-label="send"
            @click="sendMessage(questionText)"
          >
            <v-icon>mdi-send</v-icon>
          </v-btn>
        </div>
      </div>
    </div>
  </v-dialog>
</template>

<script>
import { DateTime } from 'luxon'
import { dateHelper } from '@/assets/plugins/mps-common/time/date-helper'
import { PATH } from '@/constants'
import {
  resolveMpsAiSummaryFieldLabel,
  fallbackMpsAiSummaryFieldLabel,
} from '@/constants/mpsAiSummaryLabels'

/** 채팅 버블 높이 제한 — 초과 행은 PC 안내 */
const MOBILE_AI_TABLE_MAX_ROWS = 25

const DEFAULT_SUGGESTIONS = [
  '이번 달 A/S 접수 건수 요약해줘',
  '미수금 많은 거래처 상위 알려줘',
  '만기 임박 계약이 있는 거래처는?',
]

export default {
  name: 'MobileAiChatSheet',
  props: {
    value: { type: Boolean, default: false },
  },
  data() {
    return {
      questionText: '',
      messages: [],
      loadingChat: false,
      availableCredit: null,
      loadingCreditStatus: false,
      loadingRecent: false,
      recentQuestions: [],
      suggestions: [],
      /** AI 표 행별 펼침: `${msgIdx}-${tblIdx}-${rowIdx}` → true */
      mobileAiRowExpanded: {},
    }
  },
  computed: {
    innerOpen: {
      get() {
        return this.value
      },
      set(v) {
        this.$emit('input', v)
      },
    },
    suggestionList() {
      return this.suggestions.length ? this.suggestions : DEFAULT_SUGGESTIONS
    },
    canSend() {
      return (
        !!(this.questionText && String(this.questionText).trim()) &&
        !this.loadingChat &&
        !this.chatDisabled
      )
    },
    availableCreditDisplay() {
      if (this.loadingCreditStatus) return '…'
      if (this.availableCredit == null) return '-'
      const n = Number(this.availableCredit)
      if (!Number.isFinite(n)) return '-'
      return n.toLocaleString()
    },
    chatDisabled() {
      if (this.loadingCreditStatus) return true
      if (this.availableCredit == null) return false
      const n = Number(this.availableCredit)
      if (!Number.isFinite(n)) return false
      return n <= 0
    },
    showCreditEmptyNotice() {
      return (
        this.chatDisabled &&
        !this.loadingCreditStatus &&
        !this.messages.length &&
        !this.loadingChat
      )
    },
    chatInputPlaceholder() {
      const fallbackDisabled =
        '크레딧이 부족합니다. PC웹에서 충전 후 이용해 주세요.'
      const fallbackEnabled = '질문을 입력하세요.'
      if (!this.chatDisabled) {
        return this.normalizeChatPlaceholder(
          this.$translate('질문을 입력하세요.', fallbackEnabled),
          fallbackEnabled
        )
      }
      return this.normalizeChatPlaceholder(
        this.$translate(
          '크레딧이 부족합니다. PC웹에서 충전 후 이용해 주세요.',
          fallbackDisabled
        ),
        fallbackDisabled
      )
    },
  },
  watch: {
    value(open) {
      if (open) {
        this.loadRecent()
        this.loadSuggestions()
        this.loadCreditStatus()
        this.$nextTick(() => this.scrollToTop())
      }
    },
    messages() {
      this.$nextTick(() => this.scrollToEnd())
    },
    chatDisabled(disabled) {
      if (disabled && (this.questionText === 0 || this.questionText === '0')) {
        this.questionText = ''
      }
    },
  },
  methods: {
    normalizeChatPlaceholder(value, fallback) {
      if (value == null || value === '' || value === 0 || value === '0') {
        return fallback
      }
      const s = String(value).trim()
      if (!s || s === '0') {
        return fallback
      }
      return s
    },
    close() {
      this.innerOpen = false
    },
    startNewChat() {
      this.messages = []
      this.questionText = ''
      this.mobileAiRowExpanded = {}
    },
    scrollToEnd() {
      const el = this.$refs.scrollArea
      if (el) el.scrollTop = el.scrollHeight
    },
    scrollToTop() {
      const el = this.$refs.scrollArea
      if (el) el.scrollTop = 0
    },
    async loadRecent() {
      this.loadingRecent = true
      this.recentQuestions = []
      try {
        const res = await this.$request(PATH.AI_CHAT.RECENT_QUESTIONS)
          .get()
          .appendParam('limit', 5)
          .catch()
          .enqueue()
        if (res && Array.isArray(res.questions)) {
          this.recentQuestions = res.questions
        }
      } catch (e) {
        console.warn('AI recent questions', e)
      } finally {
        this.loadingRecent = false
      }
    },
    async loadSuggestions() {
      try {
        const res = await this.$request(PATH.AI_CHAT.FAVORITE_QUESTIONS)
          .get()
          .appendParam('limit', 8)
          .catch()
          .enqueue()
        const items = Array.isArray(res && res.questions) ? res.questions : []
        this.suggestions = items.length ? items : []
      } catch (e) {
        console.warn('AI favorite questions', e)
        this.suggestions = []
      }
    },
    applyCreditStatus(payload) {
      const credit = Number(payload && payload.availableCredit)
      this.availableCredit = Number.isFinite(credit) ? credit : 0
    },
    async loadCreditStatus() {
      this.loadingCreditStatus = true
      try {
        const res = await this.$request(PATH.AI_CHAT.CREDIT_STATUS)
          .get()
          .catch()
          .enqueue()
        this.applyCreditStatus(res || {})
      } catch (e) {
        console.warn('AI credit status', e)
        this.availableCredit = null
      } finally {
        this.loadingCreditStatus = false
      }
    },
    onEnterSend() {
      if (this.canSend) this.sendMessage(this.questionText)
    },
    escapeHtml(s) {
      if (s == null) return ''
      return String(s)
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#039;')
    },
    formatInlineMarkdown(src) {
      let out = this.escapeHtml(src || '')
      out = out.replace(/`([^`]+?)`/g, '<code>$1</code>')
      out = out.replace(/\*\*\*([^*]+?)\*\*\*/g, '<strong><em>$1</em></strong>')
      out = out.replace(/\*\*([^*]+?)\*\*/g, '<strong>$1</strong>')
      out = out.replace(/\*([^*]+?)\*/g, '<em>$1</em>')
      return out
    },
    resolveTableColumns(table) {
      if (!table) return []
      const cols = table.columns
      if (Array.isArray(cols) && cols.length) return cols
      const rows = table.rows || []
      const first = rows[0]
      if (!first || typeof first !== 'object') return []
      return Object.keys(first).map((key) => ({
        key,
        title: key,
        align: typeof first[key] === 'number' ? 'right' : 'left',
      }))
    },
    isTruthyMobileFlag(v) {
      if (v === true) return true
      if (v === 1) return true
      if (typeof v === 'string') {
        const t = v.trim().toLowerCase()
        return t === 'true' || t === '1' || t === 'y'
      }
      return false
    },
    /** table_schema.yaml → API columns 의 mobile / mobileFirst */
    hasMobileSchemaHints(table) {
      return this.resolveTableColumns(table).some(
        (c) =>
          this.isTruthyMobileFlag(c.mobile) ||
          this.isTruthyMobileFlag(c.mobileFirst)
      )
    },
    /**
     * 스키마 힌트가 없고 컬럼이 많을 때(NL-SQL 등): 상단 2개 + 나머지 펼침으로 나눔.
     * (힌트가 있으면 기존 mobile/mobileFirst 규칙만 사용)
     */
    useFallbackMobileColumnSplit(table) {
      const cols = this.resolveTableColumns(table)
      return !this.hasMobileSchemaHints(table) && cols.length > 3
    },
    /** 상단 항상 표시: mobileFirst. 힌트 없으면 전체 컬럼(기존 동작). */
    primaryMobileColumns(table) {
      const cols = this.resolveTableColumns(table)
      if (this.useFallbackMobileColumnSplit(table)) {
        return cols.slice(0, 2)
      }
      if (!this.hasMobileSchemaHints(table)) return cols
      const first = cols.filter((c) => this.isTruthyMobileFlag(c.mobileFirst))
      return first.length ? first : cols
    },
    /** 펼침: mobile 이고 mobileFirst 아님. mobileFirst 가 하나도 없으면 비움. */
    secondaryMobileColumns(table) {
      const cols = this.resolveTableColumns(table)
      if (this.useFallbackMobileColumnSplit(table)) {
        return cols.slice(2)
      }
      if (!this.hasMobileSchemaHints(table)) return []
      if (!cols.some((c) => this.isTruthyMobileFlag(c.mobileFirst))) return []
      return cols.filter(
        (c) =>
          this.isTruthyMobileFlag(c.mobile) &&
          !this.isTruthyMobileFlag(c.mobileFirst)
      )
    },
    mobileAiRowExpandKey(msgIdx, tblIdx, rowIdx) {
      return `${msgIdx}-${tblIdx}-${rowIdx}`
    },
    isMobileAiRowExpanded(msgIdx, tblIdx, rowIdx) {
      return !!this.mobileAiRowExpanded[this.mobileAiRowExpandKey(msgIdx, tblIdx, rowIdx)]
    },
    toggleMobileAiRowExpand(msgIdx, tblIdx, rowIdx) {
      const k = this.mobileAiRowExpandKey(msgIdx, tblIdx, rowIdx)
      this.$set(this.mobileAiRowExpanded, k, !this.mobileAiRowExpanded[k])
    },
    onMobileAiTableRowHeadClick(msgIdx, tblIdx, rowIdx, table) {
      if (!this.secondaryMobileColumns(table).length) return
      this.toggleMobileAiRowExpand(msgIdx, tblIdx, rowIdx)
    },
    /** 접힘: 아래로, 펼침: 위로 */
    mobileAiRowExpandChevron(msgIdx, tblIdx, rowIdx) {
      return this.isMobileAiRowExpanded(msgIdx, tblIdx, rowIdx)
        ? 'mdi-chevron-up'
        : 'mdi-chevron-down'
    },
    visibleTableRows(tbl) {
      const rows = (tbl && tbl.rows) || []
      if (!Array.isArray(rows)) return []
      return rows.slice(0, MOBILE_AI_TABLE_MAX_ROWS)
    },
    tableRowsWereTruncated(tbl) {
      const rows = (tbl && tbl.rows) || []
      return Array.isArray(rows) && rows.length > MOBILE_AI_TABLE_MAX_ROWS
    },
    toIsoDateTimeStringForDateTimeCell(value) {
      if (value === null || value === undefined || value === '') return null
      if (Array.isArray(value)) {
        const y = Number(value[0])
        const m = Number(value[1])
        const d = Number(value[2])
        const hh = Number(value[3] ?? 0)
        const mm = Number(value[4] ?? 0)
        const ss = Number(value[5] ?? 0)
        if (!Number.isFinite(y) || !Number.isFinite(m) || !Number.isFinite(d))
          return null
        const dt = DateTime.fromObject({
          year: y,
          month: m,
          day: d,
          hour: hh,
          minute: mm,
          second: ss,
        })
        return dt.isValid ? dt.toISO({ suppressMilliseconds: true }) : null
      }
      if (typeof value === 'string') {
        const s = value.trim()
        if (!s) return null
        let dt = DateTime.fromISO(s)
        if (!dt.isValid) dt = DateTime.fromSQL(s)
        if (!dt.isValid) {
          const t = Date.parse(s)
          if (!Number.isNaN(t)) dt = DateTime.fromMillis(t)
        }
        return dt.isValid ? dt.toISO({ suppressMilliseconds: true }) : null
      }
      if (typeof value === 'number') {
        const ms = Math.abs(value) > 1e12 ? value : value * 1000
        const dt = DateTime.fromMillis(ms)
        return dt.isValid ? dt.toISO({ suppressMilliseconds: true }) : null
      }
      if (value instanceof Date) {
        const dt = DateTime.fromJSDate(value)
        return dt.isValid ? dt.toISO({ suppressMilliseconds: true }) : null
      }
      return null
    },
    formatAiDateTimeCellWithHelper(value) {
      const iso = this.toIsoDateTimeStringForDateTimeCell(value)
      if (!iso) return null
      if (!DateTime.fromISO(iso).isValid) return null
      const text = dateHelper.formatDateTime(iso)
      return text && String(text).trim().length ? String(text).trim() : null
    },
    toIsoDateStringForMonthCell(value) {
      if (value === null || value === undefined || value === '') return null
      if (Array.isArray(value)) {
        const y = Number(value[0])
        const m = Number(value[1])
        const d = Number(value[2] ?? 1)
        if (!Number.isFinite(y) || !Number.isFinite(m) || !Number.isFinite(d))
          return null
        return `${y}-${String(m).padStart(2, '0')}-${String(d).padStart(
          2,
          '0'
        )}`
      }
      if (typeof value === 'string') {
        const s = value.trim()
        const m = s.match(/^(\d{4}-\d{2}-\d{2})/)
        if (m) return m[1]
        const t = Date.parse(s)
        if (Number.isNaN(t)) return null
        return new Date(t).toISOString().slice(0, 10)
      }
      if (typeof value === 'number') {
        const ms = Math.abs(value) > 1e12 ? value : value * 1000
        const dt = new Date(ms)
        if (Number.isNaN(dt.getTime())) return null
        return dt.toISOString().slice(0, 10)
      }
      if (value instanceof Date) {
        if (Number.isNaN(value.getTime())) return null
        return value.toISOString().slice(0, 10)
      }
      return null
    },
    formatAiDateCellWithHelper(value) {
      const iso = this.toIsoDateStringForMonthCell(value)
      if (!iso) return null
      if (!DateTime.fromISO(iso).isValid) return null
      const text = dateHelper.formatDate(iso)
      return text && String(text).trim().length ? String(text).trim() : null
    },
    formatAiMonthCell(value) {
      const iso = this.toIsoDateStringForMonthCell(value)
      if (!iso) return null
      if (!DateTime.fromISO(iso).isValid) return null
      const text = dateHelper.formatMonth(iso)
      return text && String(text).trim().length ? String(text).trim() : null
    },
    /** 거래처 ID 등 식별자는 천 단위 구분(,) 없이 표시 */
    isAiTableAccountIdColumn(col) {
      if (!col || col.key == null || col.key === '') return false
      const k = String(col.key).trim().toLowerCase()
      return k === 'account_id' || /(^|\.)account_id$/.test(k)
    },
    formatAiTableCell(value, col) {
      if (value === null || value === undefined || value === '') return '—'
      if (typeof value === 'boolean') return value ? 'Y' : 'N'

      if (col && col.format === 'month') {
        const asMonth = this.formatAiMonthCell(value)
        if (asMonth !== null && asMonth !== undefined) return asMonth
        return '—'
      }

      if (col && col.format === 'datetime') {
        const asDt = this.formatAiDateTimeCellWithHelper(value)
        if (asDt !== null && asDt !== undefined) return asDt
        return '—'
      }

      if (col && col.format === 'date') {
        const asDate = this.formatAiDateCellWithHelper(value)
        if (asDate !== null && asDate !== undefined) return asDate
        return '—'
      }

      if (typeof value === 'object') {
        try {
          return JSON.stringify(value)
        } catch (e) {
          return String(value)
        }
      }
      if (typeof value === 'number') {
        if (col && this.isAiTableAccountIdColumn(col)) {
          return String(value)
        }
        if (col && col.format === 'number') {
          return Number.isInteger(value)
            ? value.toLocaleString()
            : value.toLocaleString(undefined, { maximumFractionDigits: 4 })
        }
        return value.toLocaleString()
      }
      return String(value)
    },
    parseSummaryNumericValue(v) {
      if (v === null || v === undefined || v === '') return null
      if (typeof v === 'number') return Number.isFinite(v) ? v : null
      if (typeof v === 'string') {
        const t = v.trim().replace(/,/g, '')
        if (t === '' || t === '-') return null
        const n = Number(t)
        return Number.isFinite(n) ? n : null
      }
      return null
    },
    isMpsAiSummaryCountKey(key) {
      const k = (key || '').toString().trim()
      if (!k) return false
      const lower = k.toLowerCase()
      if (lower === '건수' || lower === 'count' || lower === 'cnt') return true
      if (lower.endsWith('cntsum')) return false
      if (lower.endsWith('count')) return true
      if (/\bcnt$/i.test(k)) return true
      return false
    },
    isMpsAiSummaryAmountKey(key) {
      const k = (key || '').toString().trim().toLowerCase()
      if (!k) return false
      return /(price|amount|balance|fee|금액|잔액|요금|미납)/i.test(k)
    },
    isMpsAiSummaryPercentKey(key) {
      const k = (key || '').toString().trim().toLowerCase()
      if (!k) return false
      return /(rate|ratio|percent|pct|퍼센트|비율)/i.test(k)
    },
    aiResponseDomain(m) {
      const p = m && m.provenance
      const dom = p && p.queryPlan && p.queryPlan.domain
      return (dom || '').toString().trim().toLowerCase()
    },
    formatMobileAiSummaryFieldLabel(key, domain) {
      const mapped = resolveMpsAiSummaryFieldLabel(domain, key)
      if (mapped) return mapped
      return fallbackMpsAiSummaryFieldLabel(key)
    },
    formatMobileSummaryItems(summaryRows, domain) {
      if (!Array.isArray(summaryRows) || summaryRows.length === 0) return []
      const d = (domain || '').trim().toLowerCase()
      const out = []
      summaryRows.forEach((row) => {
        if (!row || typeof row !== 'object') return
        Object.keys(row).forEach((key) => {
          const v = row[key]
          if (v === undefined) return
          out.push({
            label: this.formatMobileAiSummaryFieldLabel(key, d),
            value: this.formatMobileAiSummaryValue(key, v),
          })
        })
      })
      return out
    },
    hasMobileAiTableData(message) {
      if (!message || !Array.isArray(message.tables) || !message.tables.length) return false
      return message.tables.some(
        (tbl) => tbl && Array.isArray(tbl.rows) && tbl.rows.length > 0
      )
    },
    isMobileAiQueryResultCard(card) {
      const t = (card && card.title ? card.title : '').toString().trim()
      return t === '조회 결과'
    },
    visibleMobileAiCards(message) {
      const cards = Array.isArray(message && message.cards) ? message.cards : []
      if (!cards.length) return []
      const hasTableData = this.hasMobileAiTableData(message)
      return cards.filter((card) => {
        if (!this.isMobileAiQueryResultCard(card)) return true
        return hasTableData
      })
    },
    formatMobileAiSummaryValue(key, v) {
      if (v === null || v === '') return '-'
      if (typeof v === 'boolean') return v ? 'Y' : 'N'
      if (typeof v === 'object') {
        try {
          return JSON.stringify(v)
        } catch (e) {
          return String(v)
        }
      }
      const n = this.parseSummaryNumericValue(v)
      if (n !== null) {
        if (this.isMpsAiSummaryPercentKey(key)) {
          return `${this.$currency(n)}%`
        }
        if (this.isMpsAiSummaryCountKey(key)) {
          return `${this.$currency(n)}건`
        }
        if (this.isMpsAiSummaryAmountKey(key)) {
          return `${this.$currency(n)}원`
        }
        return this.$currency(n)
      }
      return String(v)
    },
    markdownBlocks(text) {
      const lines = String(text || '').split(/\r?\n/)
      const blocks = []
      let i = 0
      while (i < lines.length) {
        const raw = lines[i] || ''
        const t = raw.trim()
        if (!t) {
          blocks.push({ type: 'br' })
          i += 1
          continue
        }
        const hm = t.match(/^#{1,4}\s+(.*)$/)
        if (hm) {
          const level = t.match(/^#+/)[0].length
          blocks.push({
            type: `h${Math.min(level, 4)}`,
            html: this.formatInlineMarkdown(hm[1] || ''),
          })
          i += 1
          continue
        }
        if (/^[-*]\s+/.test(t)) {
          const items = []
          while (i < lines.length && /^[-*]\s+/.test((lines[i] || '').trim())) {
            items.push(
              this.formatInlineMarkdown((lines[i] || '').trim().replace(/^[-*]\s+/, ''))
            )
            i += 1
          }
          blocks.push({ type: 'ul', items })
          continue
        }
        if (/^\d+\.\s+/.test(t)) {
          const items = []
          while (i < lines.length && /^\d+\.\s+/.test((lines[i] || '').trim())) {
            items.push(
              this.formatInlineMarkdown((lines[i] || '').trim().replace(/^\d+\.\s+/, ''))
            )
            i += 1
          }
          blocks.push({ type: 'ol', items })
          continue
        }
        blocks.push({ type: 'p', html: this.formatInlineMarkdown(raw) })
        i += 1
      }
      return blocks
    },
    async sendMessage(q) {
      const text = (typeof q === 'string' ? q : this.questionText || '').trim()
      if (!text || this.loadingChat || this.chatDisabled) return
      this.questionText = ''
      this.messages.push({ role: 'user', text })
      this.loadingChat = true
      try {
        const res = await this.$request(PATH.AI_CHAT.CHAT_V2)
          .post()
          .setObject({ question: text })
          .catch()
          .enqueue()
        this.messages.push({
          role: 'assistant',
          text: (res && res.answerMarkdown) || '',
          cards: (res && res.cards) || [],
          tables: (res && res.tables) || [],
          summary: (res && res.summary) || [],
          provenance: (res && res.provenance) || null,
        })
        this.loadCreditStatus()
        this.loadRecent()
      } catch (e) {
        this.messages.push({
          role: 'assistant',
          text: this.$translate(
            '요청에 실패했습니다. 네트워크 또는 서버 설정을 확인해 주세요.'
          ),
        })
      } finally {
        this.loadingChat = false
      }
    },
  },
}
</script>

<style scoped>
/* ------------------------------------------------------------------ */
/* Block: mobile-ai-chat — AI 채팅 시트 (모바일 전용 리디자인)          */
/* ------------------------------------------------------------------ */

.mobile-ai-chat {
  --mobile-ai-chat-blue-600: #2563eb;
  --mobile-ai-chat-blue-700: #1d4ed8;
  --mobile-ai-chat-blue-800: #1e40af;
  --mobile-ai-chat-surface: #f1f5f9;
  --mobile-ai-chat-card: #ffffff;
  --mobile-ai-chat-text: #0f172a;
  --mobile-ai-chat-muted: #64748b;
  --mobile-ai-chat-user-bubble: #3b82f6;
  --mobile-ai-chat-shadow-soft: 0 2px 12px rgba(15, 23, 42, 0.08);
  --mobile-ai-chat-shadow-card: 0 4px 20px rgba(15, 23, 42, 0.08);
  --mobile-ai-chat-radius-bubble: 20px;
  --mobile-ai-chat-radius-card: 16px;
  --mobile-ai-chat-radius-input: 22px;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial,
    'Noto Sans KR', 'Apple SD Gothic Neo', sans-serif;
  color: var(--mobile-ai-chat-text);
  background: var(--mobile-ai-chat-surface);
}

/* ----- 헤더 (그라데이션 블루 · 흰색 텍스트) ----- */
.mobile-ai-chat__appbar {
  flex: 0 0 auto !important;
  background: linear-gradient(
    135deg,
    var(--mobile-ai-chat-blue-600) 0%,
    var(--mobile-ai-chat-blue-700) 42%,
    var(--mobile-ai-chat-blue-800) 100%
  ) !important;
  border-bottom: none !important;
  box-shadow: 0 6px 24px rgba(37, 99, 235, 0.28);
  height: auto !important;
  padding-top: calc(8px + env(safe-area-inset-top, 0px));
  padding-bottom: 10px;
}

.mobile-ai-chat__appbar >>> .v-toolbar__content {
  flex: none !important;
  width: 100%;
  box-sizing: border-box;
  padding: 0 12px 0 12px;
  height: auto !important;
  align-items: stretch;
}

.mobile-ai-chat__header-inner {
  width: 100%;
  padding: 4px 2px 0 2px;
}

.mobile-ai-chat__appbar .mobile-ai-chat-header-title {
  color: #fff !important;
  letter-spacing: -0.02em;
}

.mobile-ai-chat__appbar .mobile-ai-chat-header-row2 {
  margin-top: 6px;
  display: flex;
  align-items: center;
  gap: 8px;
}

.mobile-ai-chat__appbar .mobile-ai-credit-label {
  color: rgba(255, 255, 255, 0.88) !important;
  font-weight: 600;
}

.mobile-ai-chat__appbar .mobile-ai-credit-text {
  color: #fff !important;
  font-weight: 700;
}

.mobile-ai-chat__disclaimer {
  margin-top: 10px;
  padding-top: 10px;
  border-top: 1px solid rgba(255, 255, 255, 0.22);
  color: rgba(255, 255, 255, 0.82) !important;
  line-height: 1.45;
}

.mobile-ai-chat__appbar >>> .mobile-ai-chat-header-btn.v-btn {
  min-width: 72px;
  height: 32px !important;
  padding: 0 12px !important;
  margin-right: 8px;
  border-color: rgba(255, 255, 255, 0.55) !important;
  background: rgba(255, 255, 255, 0.14) !important;
  color: #fff !important;
}

.mobile-ai-chat__appbar >>> .mobile-ai-chat-header-btn.v-btn .v-btn__content {
  color: #fff;
  font-weight: 600;
  font-size: 12px;
}

.mobile-ai-chat__appbar >>> .mobile-ai-chat-header-close-btn.v-btn {
  width: 36px;
  height: 36px !important;
  border: 1px solid rgba(255, 255, 255, 0.35) !important;
  background: rgba(255, 255, 255, 0.12) !important;
  border-radius: 12px;
}

.mobile-ai-chat__appbar >>> .mobile-ai-chat-header-close-btn .v-icon {
  color: #fff !important;
}

/* ----- 스크롤 영역 (연한 그레이 배경) ----- */
.mobile-ai-chat__scroll {
  -webkit-overflow-scrolling: touch;
  background: var(--mobile-ai-chat-surface);
  min-height: 0;
}

.mobile-ai-chat__scroll-inner {
  min-height: 100%;
  display: flex;
  flex-direction: column;
}

.mobile-ai-chat__scroll-inner--credit-empty {
  justify-content: center;
  align-items: center;
}

.mobile-ai-chat-credit-notice {
  width: 100%;
  max-width: 280px;
  padding: 0 24px;
  text-align: center;
  box-sizing: border-box;
}

.mobile-ai-chat-credit-notice__title {
  margin: 0 0 8px;
  font-size: 1rem;
  font-weight: 600;
  line-height: 1.45;
  color: var(--mobile-ai-chat-text);
}

.mobile-ai-chat-credit-notice__sub {
  margin: 0;
  font-size: 0.875rem;
  line-height: 1.5;
  color: var(--mobile-ai-chat-muted);
}

.mobile-ai-chat__messages {
  max-width: 100%;
  width: 100%;
}

/* ----- 메시지 행 ----- */
.mobile-ai-chat__msg {
  margin-bottom: 16px;
}

.mobile-ai-chat__msg--user {
  text-align: right;
}

.mobile-ai-chat__msg--assistant {
  text-align: left;
}

/* ----- 말풍선 ----- */
.mobile-ai-chat__bubble {
  box-shadow: var(--mobile-ai-chat-shadow-soft);
}

.mobile-ai-chat__bubble--user {
  max-width: 92%;
  text-align: left;
  border-radius: var(--mobile-ai-chat-radius-bubble) !important;
  background: linear-gradient(180deg, #3b82f6 0%, #2563eb 100%) !important;
  color: #fff !important;
  box-shadow: 0 4px 14px rgba(37, 99, 235, 0.35);
}

.mobile-ai-chat__bubble--user .body-2 {
  color: #fff;
}

.mobile-ai-chat__bubble--bot {
  max-width: 100%;
  border-radius: var(--mobile-ai-chat-radius-bubble) !important;
  background: var(--mobile-ai-chat-card) !important;
  border: 1px solid rgba(148, 163, 184, 0.25);
  box-shadow: var(--mobile-ai-chat-shadow-soft);
}

/* ----- 통계 카드 (접수 현황 등) ----- */
.mobile-ai-chat__stat-card {
  border-radius: var(--mobile-ai-chat-radius-card) !important;
  background: var(--mobile-ai-chat-card) !important;
  box-shadow: var(--mobile-ai-chat-shadow-card);
  border: none !important;
}

.mobile-ai-chat__stat-card-title {
  color: var(--mobile-ai-chat-muted);
  font-weight: 600;
  letter-spacing: -0.01em;
}

.mobile-ai-chat__stat-card-value {
  margin-top: 6px;
  font-size: 1.75rem;
  font-weight: 800;
  line-height: 1.15;
  letter-spacing: -0.03em;
  color: var(--mobile-ai-chat-text);
}

.mobile-ai-chat__stat-card-sub {
  margin-top: 8px;
  color: var(--mobile-ai-chat-muted);
  line-height: 1.35;
}

/* ----- 패널 카드 (테이블 행 · 요약) ----- */
.mobile-ai-chat__panel-card {
  border-radius: var(--mobile-ai-chat-radius-card) !important;
  background: var(--mobile-ai-chat-card) !important;
  box-shadow: var(--mobile-ai-chat-shadow-card);
  border: none !important;
}

/* 답변 카드 내 연속 목록(통계 카드·테이블 행): 항목 간 구분선 */
.mobile-ai-chat__answer-list {
  display: flex;
  flex-direction: column;
}

.mobile-ai-chat__answer-list > .mobile-ai-chat__stat-card:not(:first-child),
.mobile-ai-chat__answer-list > .mobile-ai-chat__panel-card.mobile-ai-table-row-card:not(:first-child) {
  /* .mobile-ai-chat__stat-card / __panel-card 의 border: none !important 보다 우선 */
  border-top: 1px solid rgba(148, 163, 184, 0.6) !important;
  margin-top: 12px;
  padding-top: 14px;
}

/* ----- 로딩 ----- */
.mobile-ai-chat__loading {
  border-radius: var(--mobile-ai-chat-radius-card);
  background: rgba(255, 255, 255, 0.85);
  color: var(--mobile-ai-chat-muted);
  box-shadow: var(--mobile-ai-chat-shadow-soft);
}

/* ----- 추천/최근 ----- */
.mobile-ai-chat__quick-panel {
  background: #e8edf3;
  border-top: 1px solid rgba(148, 163, 184, 0.35);
}

.mobile-ai-chat-quick-row {
  display: flex;
  align-items: flex-start;
  gap: 10px;
}

.mobile-ai-chat-quick-label {
  flex: 0 0 auto;
  line-height: 30px;
  color: var(--mobile-ai-chat-muted) !important;
  font-weight: 600;
}

.mobile-ai-chat-quick-scroll {
  flex: 1 1 auto;
  min-width: 0;
  display: flex;
  flex-wrap: nowrap;
  align-items: center;
  overflow-x: auto;
  overflow-y: hidden;
  -webkit-overflow-scrolling: touch;
}

.mobile-ai-chat-quick-scroll::-webkit-scrollbar {
  height: 4px;
}

.mobile-ai-chat-quick-chip {
  flex: 0 0 auto;
  margin-bottom: 0 !important;
  border-radius: 999px !important;
}

.mobile-ai-chat-quick-loading {
  line-height: 30px;
  color: var(--mobile-ai-chat-muted) !important;
}

/* ----- 하단 입력 (고정 느낌 · 둥근 필드 · 원형 전송) ----- */
.mobile-ai-chat__footer {
  background: var(--mobile-ai-chat-card);
  border-top: 1px solid rgba(148, 163, 184, 0.35);
  box-shadow: 0 -8px 28px rgba(15, 23, 42, 0.08);
  z-index: 2;
}

.mobile-ai-chat__composer {
  gap: 0;
  align-items: flex-end;
}

.mobile-ai-chat__input >>> .v-input__slot {
  margin-bottom: 0 !important;
  min-height: 48px !important;
  align-items: center;
  padding: 8px 16px !important;
  border-radius: var(--mobile-ai-chat-radius-input) !important;
  background: #f8fafc !important;
}

.mobile-ai-chat__input >>> fieldset {
  border-radius: var(--mobile-ai-chat-radius-input) !important;
  border-color: #e2e8f0 !important;
}

.mobile-ai-chat__input >>> .v-text-field__slot textarea {
  margin-top: 6px !important;
  margin-bottom: 6px !important;
  line-height: 1.45;
  font-size: 15px;
}

.mobile-ai-chat__send-btn {
  width: 48px !important;
  height: 48px !important;
  min-width: 48px !important;
  box-shadow: 0 4px 14px rgba(37, 99, 235, 0.4) !important;
}

.mobile-ai-chat__send-btn >>> .v-btn__content .v-icon {
  color: #fff;
}

.pb-safe {
  padding-bottom: calc(12px + env(safe-area-inset-bottom, 0px));
}

/* ----- 마크다운 ----- */
.mobile-ai-md-p {
  margin: 0 0 8px 0;
  line-height: 1.5;
  white-space: normal;
  word-break: break-word;
  color: var(--mobile-ai-chat-text);
}

.mobile-ai-md-h1,
.mobile-ai-md-h2,
.mobile-ai-md-h3,
.mobile-ai-md-h4 {
  margin: 10px 0 8px 0;
  line-height: 1.35;
  font-weight: 700;
  color: var(--mobile-ai-chat-text);
}

.mobile-ai-md-h1 {
  font-size: 1.05rem;
}
.mobile-ai-md-h2 {
  font-size: 1.02rem;
}
.mobile-ai-md-h3 {
  font-size: 0.98rem;
}
.mobile-ai-md-h4 {
  font-size: 0.95rem;
}

.mobile-ai-md-list {
  margin: 0 0 10px 18px;
  padding: 0;
  color: var(--mobile-ai-chat-text);
}

.mobile-ai-md-list li {
  margin: 0 0 4px 0;
  line-height: 1.5;
}

.mobile-ai-md-br {
  height: 8px;
}

/* ----- 테이블 카드 내부 ----- */
.mobile-ai-table-row-card {
  background: var(--mobile-ai-chat-card) !important;
}

.mobile-ai-table-row-inline {
  display: flex;
  align-items: center;
  flex-wrap: nowrap;
  gap: 8px;
  min-width: 0;
  line-height: 1.4;
}

.mobile-ai-table-row-head {
  gap: 4px;
}

.mobile-ai-table-row-head--interactive {
  cursor: pointer;
  margin: -8px -8px;
  padding: 8px 8px;
  border-radius: 12px;
}

.mobile-ai-table-row-head--interactive:active {
  background: rgba(37, 99, 235, 0.06);
}

.mobile-ai-table-row-chevron {
  margin-left: 4px;
  margin-right: -4px;
}

.mobile-ai-table-secondary-wrap {
  padding-top: 10px;
}

.mobile-ai-table-row-inline + .mobile-ai-table-row-inline {
  margin-top: 8px;
  padding-top: 8px;
  border-top: 1px solid rgba(148, 163, 184, 0.25);
}

.mobile-ai-table-row-inline-label {
  flex-shrink: 0;
  max-width: 42%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  color: var(--mobile-ai-chat-muted) !important;
}

.mobile-ai-table-row-inline-label::after {
  content: ':';
}

.mobile-ai-table-row-inline-value {
  flex: 1 1 0;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-weight: 600;
  color: var(--mobile-ai-chat-text);
}

.mobile-ai-table-field-secondary + .mobile-ai-table-field-secondary {
  border-top: 1px solid rgba(148, 163, 184, 0.28);
  margin-top: 10px;
  padding-top: 10px;
}

.mobile-ai-table-cell-value {
  margin-top: 4px;
  line-height: 1.45;
  word-break: break-word;
  white-space: pre-wrap;
}

.mobile-ai-table-cell-value--clamped {
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
  line-clamp: 2;
  overflow: hidden;
  word-break: break-word;
  white-space: normal;
}

/* ----- 요약 카드 텍스트 ----- */
.mobile-ai-summary-title {
  color: var(--mobile-ai-chat-muted);
  font-weight: 700;
}

.mobile-ai-summary-items,
.mobile-ai-summary-item {
  color: var(--mobile-ai-chat-text);
}

@media (max-width: 360px) {
  .mobile-ai-chat__stat-card-value {
    font-size: 1.5rem;
  }
}
</style>

<style>
.mobile-ai-chat-dialog {
  margin: 0 !important;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial,
    'Noto Sans KR', 'Apple SD Gothic Neo', sans-serif;
}
</style>
